import GoogleClassroomService from '@learnics/services/src/googleClassroom'
import { validateAndRefreshOauth2TokenIfNeeded } from '@/utils/gapi/validateAndRefreshOauth2TokenIfNeeded'
import {
  DEFAULT_OAUTH_SCOPES,
  OauthProviderIds,
} from '@learnics/models/src/utils/oauthConfig.mjs'
import { authorizeOauthScopes } from '@/utils/gapi/authorizeOauthScopes'

async function validateHelper(scopes, validationOnly = false) {
  let token
  let allScopes = [...DEFAULT_OAUTH_SCOPES[OauthProviderIds.GOOGLE], ...scopes]
  if (validationOnly) {
    token = await validateAndRefreshOauth2TokenIfNeeded(
      OauthProviderIds.GOOGLE,
      scopes,
    )
  } else {
    token = await authorizeOauthScopes(OauthProviderIds.GOOGLE, scopes)
  }
  return token.access_token
}

/**
 * A wrapper around GoogleClassroomService that handles authorization
 * for each of the API calls - specifically for learnics-app.
 */
export default {
  async listCourses(validationOnly = false) {
    const accessToken = await validateHelper(
      ['https://www.googleapis.com/auth/classroom.courses.readonly'],
      validationOnly,
    )
    return await GoogleClassroomService.listCourses(accessToken)
  },

  async createCourseWork(courseId, courseworkObject, validationOnly = false) {
    const accessToken = await validateHelper(
      ['https://www.googleapis.com/auth/classroom.coursework.students'],
      validationOnly,
    )
    return await GoogleClassroomService.createCourseWork(
      accessToken,
      courseId,
      courseworkObject,
    )
  },
  async createCourseOrSection(
    aliasId,
    courseName,
    sectionName,
    validationOnly = false,
  ) {
    const accessToken = await validateHelper(
      ['https://www.googleapis.com/auth/classroom.courses'],
      validationOnly,
    )
    return await GoogleClassroomService.createCourse(
      accessToken,
      aliasId,
      courseName,
      sectionName,
    )
  },
  async updateCourseWork(
    courseId,
    courseworkId,
    updateMask,
    courseworkObject,
    validationOnly = false,
  ) {
    const accessToken = await validateHelper(
      ['https://www.googleapis.com/auth/classroom.coursework.students'],
      validationOnly,
    )
    return await GoogleClassroomService.updateCourseWork(
      accessToken,
      courseId,
      courseworkId,
      updateMask,
      courseworkObject,
    )
  },

  async listCourseWork(courseId, validationOnly = false) {
    const scopes = [
      [
        'https://www.googleapis.com/auth/classroom.coursework.students.readonly',
        'https://www.googleapis.com/auth/classroom.student-submissions.students.readonly',
        'https://www.googleapis.com/auth/classroom.coursework.students',
      ],
    ]
    const accessToken = await validateHelper(scopes, validationOnly)
    return await GoogleClassroomService.listCourseWork(accessToken, courseId)
  },
  async registerCourseworkPushNotifications(courseId, validationOnly = false) {
    const accessToken = await validateHelper(
      ['https://www.googleapis.com/auth/classroom.push-notifications'],
      validationOnly,
    )
    return await GoogleClassroomService.registerCourseworkPushNotifications(
      accessToken,
      courseId,
    )
  },

  async getCourseFromClassroomUrl(
    courseUrl,
    courses = null,
    validationOnly = false,
  ) {
    if (!courses) {
      try {
        courses = await this.listCourses(validationOnly)
      } catch (err) {
        console.log('Error getting courses from classroom URL', err)
        return null
      }
    }
    const splitRegex = /(https?:\/\/)(.*?)(\/.*)/
    const endOfUrl = splitRegex.exec(courseUrl).pop()
    const possibilities = endOfUrl.split('/').filter((i) => {
      return i.length > 3
    })
    const courseIdentifiers = possibilities.reverse()

    let course = null
    for (let i = 0; i < courseIdentifiers.length && !course && courses; i++) {
      course = courses.find(
        (c) => c.alternateLink.indexOf(courseIdentifiers[i]) >= 0,
      )
    }
    return course
  },
}
